export type CampaignRewards = {
  experience: number;
  coins: number;
  lives?: number;
  inventory?: { fiftyFifty: number; freeze: number; swapFrame: number };
};
type RewardChapter = {
  difficulty: 1 | 2 | 3 | 4 | 5;
  questionIds: readonly string[];
  totalRewards?: { exp: number; coins: number };
  earnedRewards?: { exp: number; coins: number };
};

// Preview of the starting economy in docs/game-mechanics.md; grants remain server-owned.
const coinsByDifficulty = { 1: 15, 2: 20, 3: 25, 4: 30, 5: 40 } as const;

export const getChapterRewards = (chapter: RewardChapter): CampaignRewards =>
  chapter.totalRewards
    ? { experience: chapter.totalRewards.exp, coins: chapter.totalRewards.coins }
    : {
        experience: chapter.questionIds.length,
        coins: coinsByDifficulty[chapter.difficulty],
      };

export const getEarnedCampaignRewards = (chapters: readonly RewardChapter[]): CampaignRewards | undefined =>
  chapters.every((chapter) => chapter.earnedRewards !== undefined)
    ? chapters.reduce(
        (sum, chapter) => ({
          experience: sum.experience + chapter.earnedRewards!.exp,
          coins: sum.coins + chapter.earnedRewards!.coins,
        }),
        { experience: 0, coins: 0 },
      )
    : undefined;

// Completion gifts preview the configured starting economy; actual grants always come from answer.rewards.
export const getChapterCompletionRewards = (difficulty: RewardChapter['difficulty']) => ({
  lives: 1,
  inventory: {
    fiftyFifty: difficulty === 5 ? 1 : 0,
    freeze: difficulty >= 3 ? 1 : 0,
    swapFrame: difficulty <= 2 || difficulty === 5 ? 1 : 0,
  },
});

export const getCampaignRewards = (chapters: readonly RewardChapter[]): CampaignRewards =>
  chapters.reduce<CampaignRewards>(
    (total, chapter) => {
      const reward = getChapterRewards(chapter);
      const gifts = getChapterCompletionRewards(chapter.difficulty);
      return {
        experience: total.experience + reward.experience,
        coins: total.coins + reward.coins,
        lives: (total.lives ?? 0) + gifts.lives,
        inventory: {
          fiftyFifty: (total.inventory?.fiftyFifty ?? 0) + gifts.inventory.fiftyFifty,
          freeze: (total.inventory?.freeze ?? 0) + gifts.inventory.freeze,
          swapFrame: (total.inventory?.swapFrame ?? 0) + gifts.inventory.swapFrame,
        },
      };
    },
    { experience: 0, coins: 0 },
  );
